<template>
  <div class="shop-page">
    <div class="container">
      <div class="picks-notice">
        <p>
          Looking to purchase picks?
          <router-link to="/packages/all">
            Click here to Buy Premium Picks!
          </router-link>
        </p>
      </div>

      <div v-if="loading" class="loading">
        Loading products...
      </div>

      <div v-else-if="error" class="error">
        {{ error }}
      </div>

      <div v-else-if="products.length === 0" class="empty-state">
        <p>No products available at the moment.</p>
      </div>

      <div v-else class="product-grid">
        <div
          v-for="product in products"
          :key="product.id"
          class="product-card"
          @click="viewProduct(product)"
        >
          <div class="product-image">
            <div v-if="product.is_on_sale" class="sale-badge">
              SALE
            </div>
            <img
              v-if="primaryImage(product)"
              :src="primaryImage(product).image_url"
              :alt="product.name"
            />
            <div v-else class="no-image">
              No Image
            </div>
          </div>

          <div class="product-info">
            <h3 class="product-name">
              {{ product.name }}
            </h3>
            <p class="product-description">
              {{ truncateDescription(product.description) }}
            </p>
            <div class="product-footer">
              <div class="price-container">
                <span v-if="product.is_on_sale" class="product-price-regular">
                  ${{ formatPrice(product.base_price_cents) }}
                </span>
                <span class="product-price" :class="{ 'product-price-sale': product.is_on_sale }">
                  ${{ formatPrice(product.is_on_sale ? product.sale_price_cents : product.base_price_cents) }}
                </span>
                <span v-if="product.is_on_sale && product.discount_percentage" class="discount-badge">
                  -{{ product.discount_percentage }}%
                </span>
              </div>
              <button class="btn-view">
                View Details
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'

export default {
  name: 'Shop',
  data () {
    return {
      loading: false,
      error: null
    }
  },
  computed: {
    ...mapState(['products'])
  },
  mounted () {
    this.loadProducts()
  },
  methods: {
    loadProducts () {
      this.loading = true
      this.error = null

      this.$store.dispatch('getProducts')
        .then(() => {
          this.loading = false
        })
        .catch((err) => {
          this.loading = false
          this.error = 'Failed to load products. Please try again later.'
          console.error('Error loading products:', err)
        })
    },
    viewProduct (product) {
      this.$router.push(`/shop/${product.slug}`)
    },
    primaryImage (product) {
      if (!product.images || product.images.length === 0) return null
      return product.images.find(img => img.is_primary === 1) || product.images[0]
    },
    formatPrice (cents) {
      return (cents / 100).toFixed(2)
    },
    truncateDescription (description) {
      if (!description) return ''
      return description.length > 100 ? description.substring(0, 100) + '...' : description
    }
  }
}
</script>

<style scoped>
.shop-page {
  padding: 20px 0;
  min-height: 60vh;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

.picks-notice {
  background: #edf2f7;
  border-left: 4px solid #3182ce;
  border-radius: 4px;
  padding: 16px;
  margin-bottom: 30px;
  text-align: center;
}

.picks-notice p {
  margin: 0;
  font-size: 16px;
  color: #2d3748;
}

.picks-notice a {
  color: #3182ce;
  font-weight: 600;
  text-decoration: none;
}

.picks-notice a:hover {
  text-decoration: underline;
}

.loading,
.error,
.empty-state {
  text-align: center;
  padding: 40px 20px;
  font-size: 18px;
}

.error {
  color: #e53e3e;
}

.product-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 24px;
  margin-top: 20px;
}

.product-card {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;
  background: white;
}

.product-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.product-image {
  width: 100%;
  height: 300px;
  overflow: hidden;
  background: #f7fafc;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

.product-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.no-image {
  color: #a0aec0;
  font-size: 14px;
}

/* Sale Badge */
.sale-badge {
  position: absolute;
  top: 12px;
  right: 12px;
  background: #e53e3e;
  color: white;
  padding: 6px 12px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: bold;
  z-index: 1;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.product-info {
  padding: 16px;
}

.product-name {
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 8px 0;
  color: #2d3748;
}

.product-description {
  font-size: 14px;
  color: #718096;
  margin: 0 0 12px 0;
  line-height: 1.5;
}

.product-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 12px;
}

/* Price Container */
.price-container {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

/* Regular Price (strikethrough when on sale) */
.product-price-regular {
  font-size: 16px;
  color: #a0aec0;
  text-decoration: line-through;
}

.product-price {
  font-size: 20px;
  font-weight: bold;
  color: #2b6cb0;
}

/* Sale Price Styling */
.product-price-sale {
  color: #e53e3e !important;
  font-weight: bold;
}

/* Discount Badge */
.discount-badge {
  background: #48bb78;
  color: white;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
}

.btn-view {
  background: #3182ce;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  transition: background 0.2s;
}

.btn-view:hover {
  background: #2c5aa0;
}

@media (max-width: 768px) {
  .product-grid {
    grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
    gap: 16px;
  }

  .page-title {
    font-size: 24px;
  }
}
</style>



// WEBPACK FOOTER //
// Shop.vue?48bcc6dc